/**
 * Events shown on a real calendar. FullCalendar handles month/week/day
 * + agenda views; the rrule plugin expands `daily` / `weekly`
 * recurrences server-rendered-style, so we just translate our
 * EventDoc shape and hand it over. Click a day to add; click an event
 * to edit.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';
import rrulePlugin from '@fullcalendar/rrule';
import type { DateSelectArg, DatesSetArg, EventDropArg } from '@fullcalendar/core';
import type { EventClickArg, EventContentArg, EventInput } from '@fullcalendar/core';
import type { EventResizeDoneArg } from '@fullcalendar/interaction';
import { Timestamp } from 'firebase/firestore';

import { useAccount } from '@/hooks/useAccount';
import { useEvents, type EventWithId } from '@/hooks/useEvents';
import { Spinner } from '@/components/ui/Spinner';
import { Button } from '@/components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/Dialog';
import { LoadErrorBanner } from '@/components/LoadErrorBanner';
import { toFcEvents, EVENT_COLOR, REMINDER_COLOR } from '@/lib/eventToFcEvent';
import { moveRecurringOccurrence, upsertEvent } from '@/lib/firestore';
import {
  safeTimeZone, todayInTz, instantToDateStr, instantToTimeStr,
  fcLocalDateToInstant, inclusiveAllDayEndDate, reanchorRecurringInstant,
} from '@/lib/timeZone';
import { addDays } from '@/lib/dateUtil';
import {
  EventFormDialog,
  EMPTY_FORM,
  type EventFormInitial,
} from '@/components/EventFormDialog';

// Shown when a drag-to-move/resize write fails and the event snaps back, so the
// silent revert is explained. Covers both online failures and offline — an
// offline write throws OfflineError fast (firestoreWithRetry), so it lands here
// too. The generic wording fits both; the sticky OfflineBanner adds the offline
// reason.
const DRAG_SAVE_FAILED_MSG =
  "Couldn't save the change — moved it back. Please try again.";

export function EventsCalendarPage() {
  const { aid, cid } = useParams<{ aid: string; cid: string }>();
  const {
    events,
    loading,
    error: eventsError,
    retry: retryEvents,
  } = useEvents(aid ?? null, cid ?? null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogInitial, setDialogInitial] = useState<EventFormInitial>(EMPTY_FORM);
  // Transient notice when a drag-save reverts; auto-clears after a few seconds.
  const [dragSaveError, setDragSaveError] = useState<string | null>(null);
  useEffect(() => {
    if (!dragSaveError) return;
    const t = setTimeout(() => setDragSaveError(null), 6000);
    return () => clearTimeout(t);
  }, [dragSaveError]);

  const isMobile = useIsMobile();
  // Week view by default; the caregiver's last-picked view is restored
  // from localStorage across sessions. Read once on mount — FullCalendar
  // only honours initialView at mount, and datesSet keeps storage in
  // sync as the view changes.
  const initialViewRef = useRef<string>(readStoredView());

  // The elder's account timezone drives ALL calendar time interpretation:
  // events are entered, displayed, and edited as wall-clock in this zone, not
  // the caregiver's browser zone (see #141). FullCalendar stays in local mode
  // and is fed account-tz wall-clock as floating strings.
  const { account } = useAccount(aid ?? null);
  const tz = safeTimeZone(account?.timeZone);

  const fcEvents: EventInput[] = useMemo(() => toFcEvents(events, tz), [events, tz]);

  const onDatesSet = useCallback((arg: DatesSetArg) => {
    writeStoredView(arg.view.type);
  }, []);

  // Shrink the time-grid (week/day views) to roughly the elder's waking
  // hours so it isn't a full 24h tall on mobile. Padded an hour each
  // side; events outside this window still show in the month and list
  // views (the grid hides slots outside [slotMinTime, slotMaxTime]).
  const dayWindow = useMemo(() => {
    const wake = account?.wakeHour ?? 7;
    const bed = account?.bedtimeHour ?? 21;
    const pad = (h: number) => `${String(h).padStart(2, '0')}:00:00`;
    return {
      slotMinTime: pad(Math.max(0, Math.min(wake, bed) - 1)),
      slotMaxTime: pad(Math.min(24, Math.max(wake, bed) + 1)),
    };
  }, [account?.wakeHour, account?.bedtimeHour]);

  const openCreate = useCallback(
    (init: Partial<EventFormInitial>) => {
      // Default a new event to the elder's today, not the caregiver's.
      const startDate = init.startDate ?? todayInTz(tz);
      setDialogInitial({
        ...EMPTY_FORM,
        startDate,
        endDate: init.endDate ?? startDate,
        ...init,
      });
      setDialogOpen(true);
    },
    [tz],
  );

  // FullCalendar fires `select` on both clicks and drags when
  // selectable=true. Click → single cell. Drag → range.
  // - allDay range: arg.end is exclusive, so subtract 1 day to get
  //   the inclusive last day for our schema.
  // - timed range: pre-fill startTime + endTime; for cross-midnight
  //   drags (rare), endDate is the calendar day of arg.end.
  const onSelect = useCallback(
    (arg: DateSelectArg) => {
      if (arg.allDay) {
        const start = toIsoLocal(arg.start);
        // arg.end is the exclusive next-midnight; step back one calendar day
        // via string arithmetic — DST-safe, unlike subtracting 86_400_000 ms
        // (a 23h spring-forward day would overshoot into the prior day).
        const inclusiveEnd = addDays(toIsoLocal(arg.end), -1);
        const end = inclusiveEnd < start ? start : inclusiveEnd;
        openCreate({ startDate: start, endDate: end, allDay: true });
      } else {
        openCreate({
          startDate: toIsoLocal(arg.start),
          endDate: toIsoLocal(arg.end),
          startTime: toHmLocal(arg.start),
          endTime: toHmLocal(arg.end),
          allDay: false,
        });
      }
    },
    [openCreate],
  );

  const onEventClick = useCallback(
    (arg: EventClickArg) => {
      const src = arg.event.extendedProps['sourceEvent'] as EventWithId | undefined;
      if (!src) return;
      // arg.event.start is the OCCURRENCE start (FullCalendar resolves
      // recurring events into per-day instances). For per-occurrence
      // delete, we need the local-tz date of *this* occurrence, not
      // the source's first instance.
      const occurrenceDate = arg.event.start
        ? toIsoLocal(arg.event.start)
        : undefined;
      const initial = eventToFormInitial(src, tz);
      setDialogInitial(occurrenceDate ? { ...initial, occurrenceDate } : initial);
      setDialogOpen(true);
    },
    [tz],
  );

  // Drag-to-move (eventDrop) and drag-to-resize (eventResize). For a
  // non-recurring source the change is applied directly. For a
  // recurring source the user is prompted via the
  // recurringChange dialog: "this occurrence" creates an exception +
  // a new non-recurring event at the new time; "all occurrences"
  // shifts the source's startsAt / endsAt by the drag delta.
  const [recurringChange, setRecurringChange] = useState<RecurringChange | null>(null);

  const applyDirectChange = useCallback(
    async (src: EventWithId, arg: EventDropArg | EventResizeDoneArg) => {
      if (!aid || !cid || !arg.event.start) {
        arg.revert();
        return;
      }
      try {
        await upsertEvent(aid, cid, src.id, {
          ...newEventFields(src),
          title: src.title,
          startsAt: fcDateToInstant(arg.event.start, tz),
          endsAt: arg.event.end ? fcDateToInstant(arg.event.end, tz) : undefined,
          allDay: src.allDay,
          recurrence: src.recurrence,
          recurrenceUntil: src.recurrenceUntil,
          excludedDates: src.excludedDates,
        });
      } catch {
        arg.revert();
        setDragSaveError(DRAG_SAVE_FAILED_MSG);
      }
    },
    [aid, cid, tz],
  );

  const onEventDrop = useCallback(
    (arg: EventDropArg) => {
      const src = arg.event.extendedProps['sourceEvent'] as EventWithId | undefined;
      if (!src || !aid || !cid || !arg.event.start) {
        arg.revert();
        return;
      }
      if (isRecurringSource(src)) {
        setRecurringChange({ kind: 'drop', arg, src });
        return;
      }
      void applyDirectChange(src, arg);
    },
    [aid, cid, applyDirectChange],
  );

  const onEventResize = useCallback(
    (arg: EventResizeDoneArg) => {
      const src = arg.event.extendedProps['sourceEvent'] as EventWithId | undefined;
      if (!src || !aid || !cid || !arg.event.start || !arg.event.end) {
        arg.revert();
        return;
      }
      if (isRecurringSource(src)) {
        setRecurringChange({ kind: 'resize', arg, src });
        return;
      }
      void applyDirectChange(src, arg);
    },
    [aid, cid, applyDirectChange],
  );

  const cancelRecurringChange = useCallback(() => {
    if (recurringChange) recurringChange.arg.revert();
    setRecurringChange(null);
  }, [recurringChange]);

  const applyToAllOccurrences = useCallback(async () => {
    if (!recurringChange || !aid || !cid) return;
    const { arg, src } = recurringChange;
    const newStart = arg.event.start;
    const oldStart = arg.oldEvent.start;
    if (!newStart || !oldStart) {
      arg.revert();
      setRecurringChange(null);
      return;
    }
    try {
      // Re-anchor the whole series by the WALL-CLOCK shift the drag applied,
      // not an absolute ms delta: the delta drifts by an hour when the series
      // anchor sits on a different UTC offset (DST) than the dragged occurrence.
      // ("This occurrence" already re-anchors correctly via fcDateToInstant.)
      // Drop moves start+end together; resize leaves start put (oldStart ===
      // newStart ⇒ re-derives the same anchor) and re-anchors only the end.
      const startAnchor = (src.startsAt as { toDate: () => Date }).toDate();
      const newStartsAt = reanchorRecurringInstant(startAnchor, oldStart, newStart, tz);
      const oldEndsAt = src.endsAt as { toDate?: () => Date } | undefined;
      const newEndsAt =
        oldEndsAt?.toDate && arg.event.end && arg.oldEvent.end
          ? reanchorRecurringInstant(oldEndsAt.toDate(), arg.oldEvent.end, arg.event.end, tz)
          : null;
      await upsertEvent(aid, cid, src.id, {
        ...newEventFields(src),
        title: src.title,
        startsAt: Timestamp.fromDate(newStartsAt),
        endsAt: newEndsAt ? Timestamp.fromDate(newEndsAt) : undefined,
        allDay: src.allDay,
        recurrence: src.recurrence,
        recurrenceUntil: src.recurrenceUntil,
        excludedDates: src.excludedDates,
      });
    } catch {
      arg.revert();
      setDragSaveError(DRAG_SAVE_FAILED_MSG);
    }
    setRecurringChange(null);
  }, [recurringChange, aid, cid, tz]);

  const applyToThisOccurrence = useCallback(async () => {
    if (!recurringChange || !aid || !cid) return;
    const { arg, src } = recurringChange;
    const newStart = arg.event.start;
    const oldStart = arg.oldEvent.start;
    if (!newStart || !oldStart) {
      arg.revert();
      setRecurringChange(null);
      return;
    }
    try {
      // Exclude the OLD occurrence from the series AND create a replacement
      // non-recurring event at the new time — atomically (one batch), so a
      // partial failure can't delete the occurrence with no replacement and the
      // revert below stays truthful.
      const occurrenceIso = toIsoLocal(oldStart);
      const excluded = Array.from(
        new Set([...(src.excludedDates ?? []), occurrenceIso]),
      );
      await moveRecurringOccurrence(aid, cid, src.id, excluded, {
        ...newEventFields(src),
        title: src.title,
        startsAt: fcDateToInstant(newStart, tz),
        endsAt: arg.event.end ? fcDateToInstant(arg.event.end, tz) : undefined,
        allDay: src.allDay,
        recurrence: 'none',
      });
    } catch {
      arg.revert();
      setDragSaveError(DRAG_SAVE_FAILED_MSG);
    }
    setRecurringChange(null);
  }, [recurringChange, aid, cid, tz]);

  return (
    <div className="mx-auto max-w-6xl">
      <div className="mb-4 flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-ink">Events</h1>
          <p className="mt-1 text-sm text-ink-soft">
            Click a day to add an event. Click an event to edit it.
          </p>
        </div>
        <Button onClick={() => openCreate({})}>+ Add event</Button>
      </div>

      <Legend />

      {dragSaveError && (
        <div
          role="alert"
          className="mb-4 flex items-center justify-between gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
        >
          <span>{dragSaveError}</span>
          <button
            type="button"
            onClick={() => setDragSaveError(null)}
            className="shrink-0 font-medium text-red-700 underline hover:no-underline"
          >
            Dismiss
          </button>
        </div>
      )}

      {eventsError && (
        <LoadErrorBanner what="the calendar" onRetry={retryEvents} />
      )}

      {loading ? (
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      ) : (
        <div className="rmv-calendar rounded-card border border-paper-border bg-white p-3 shadow-card">
          <FullCalendar
            plugins={[
              dayGridPlugin,
              timeGridPlugin,
              listPlugin,
              interactionPlugin,
              rrulePlugin,
            ]}
            initialView={initialViewRef.current}
            datesSet={onDatesSet}
            slotMinTime={dayWindow.slotMinTime}
            slotMaxTime={dayWindow.slotMaxTime}
            headerToolbar={{
              left: 'prev,next today',
              center: 'title',
              right: isMobile
                ? 'dayGridMonth,timeGridWeek,listWeek'
                : 'dayGridMonth,timeGridWeek,timeGridDay,listWeek',
            }}
            events={fcEvents}
            eventContent={renderEventContent}
            selectable
            selectMirror
            select={onSelect}
            eventClick={onEventClick}
            editable
            eventDrop={onEventDrop}
            eventResize={onEventResize}
            longPressDelay={250}
            selectLongPressDelay={250}
            height="auto"
            firstDay={1}
            nowIndicator
            dayMaxEvents
            allDaySlot
            stickyHeaderDates
          />
        </div>
      )}

      {aid && cid && (
        <EventFormDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          initial={dialogInitial}
          aid={aid}
          cid={cid}
          timeZone={tz}
        />
      )}

      <Dialog
        open={!!recurringChange}
        onOpenChange={(open) => {
          if (!open) cancelRecurringChange();
        }}
      >
        <DialogContent className="max-w-md">
          <DialogTitle>
            {recurringChange?.kind === 'resize' ? 'Resize recurring event?' : 'Move recurring event?'}
          </DialogTitle>
          <DialogDescription className="mt-1">
            <span className="font-medium text-ink">{recurringChange?.src.title}</span>{' '}
            is part of a {recurringChange?.src.recurrence} series. Apply this change to:
          </DialogDescription>
          <div className="mt-4 flex flex-col gap-2">
            <Button onClick={() => void applyToThisOccurrence()}>
              Just this occurrence
            </Button>
            <Button onClick={() => void applyToAllOccurrences()}>
              All occurrences
            </Button>
            <Button variant="ghost" onClick={cancelRecurringChange}>
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** First-run checklist removed — caregivers find their way around
 * via the top nav, and the Settings page surfaces device pairing
 * directly. */

function Legend() {
  return (
    <div className="mb-3 flex flex-wrap gap-3 text-xs text-ink-muted">
      <span className="flex items-center gap-1.5">
        <span
          className="inline-block h-3 w-3 rounded-sm"
          style={{ backgroundColor: EVENT_COLOR }}
        />
        Event (appears in the daily summary)
      </span>
      <span className="flex items-center gap-1.5">
        <span
          className="inline-block h-3 w-3 rounded-sm"
          style={{ backgroundColor: REMINDER_COLOR }}
        />
        Reminder (spoken on button press, within its time window)
      </span>
    </div>
  );
}

// Persisted calendar view. Week is the default for first-time use; the
// caregiver's last selection is remembered across sessions in
// localStorage (vs sessionStorage, which wouldn't survive a fresh tab).
const CALENDAR_VIEW_KEY = 'rmv:calendar-view';
const DEFAULT_CALENDAR_VIEW = 'timeGridWeek';
const VALID_CALENDAR_VIEWS = new Set([
  'dayGridMonth',
  'timeGridWeek',
  'timeGridDay',
  'listWeek',
]);

function readStoredView(): string {
  if (typeof window === 'undefined') return DEFAULT_CALENDAR_VIEW;
  try {
    const v = window.localStorage.getItem(CALENDAR_VIEW_KEY);
    return v && VALID_CALENDAR_VIEWS.has(v) ? v : DEFAULT_CALENDAR_VIEW;
  } catch {
    return DEFAULT_CALENDAR_VIEW;
  }
}

function writeStoredView(view: string): void {
  if (typeof window === 'undefined' || !VALID_CALENDAR_VIEWS.has(view)) return;
  try {
    window.localStorage.setItem(CALENDAR_VIEW_KEY, view);
  } catch {
    /* quota / disabled storage — best-effort */
  }
}

function useIsMobile(threshold = 768): boolean {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window === 'undefined' ? false : window.innerWidth < threshold,
  );
  const t = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    function onResize() {
      if (t.current) clearTimeout(t.current);
      t.current = setTimeout(() => setIsMobile(window.innerWidth < threshold), 150);
    }
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      if (t.current) clearTimeout(t.current);
    };
  }, [threshold]);
  return isMobile;
}

type RecurringChange =
  | { kind: 'drop'; arg: EventDropArg; src: EventWithId }
  | { kind: 'resize'; arg: EventResizeDoneArg; src: EventWithId };

function isRecurringSource(src: EventWithId): boolean {
  return !!(src.recurrence && src.recurrence !== 'none');
}

function eventToFormInitial(ev: EventWithId, tz: string): EventFormInitial {
  const startsAtDate = (ev.startsAt as { toDate?: () => Date })?.toDate?.() ?? null;
  const endsAtDate =
    ev.endsAt
      ? ((ev.endsAt as { toDate?: () => Date })?.toDate?.() ?? null)
      : null;
  // Read every stored instant back as wall-clock in the ELDER's account tz.
  const startDate = startsAtDate ? instantToDateStr(startsAtDate, tz) : todayInTz(tz);
  // For all-day events, endsAt is exclusive (next day after the last included
  // day). Step back one calendar day (string arithmetic — DST-safe, unlike a
  // raw -86_400_000 ms subtraction once interpreted in the account tz).
  const endDate = (() => {
    if (!endsAtDate) return startDate;
    if (ev.allDay) {
      const inclusive = inclusiveAllDayEndDate(endsAtDate, tz);
      return inclusive < startDate ? startDate : inclusive; // ISO strings sort as dates
    }
    return instantToDateStr(endsAtDate, tz);
  })();
  const kind: 'event' | 'interval' | 'reminder' = ev.kind ?? 'event';
  const includeFromDaysBefore = ev.includeFromDaysBefore;
  const notifications = ev.notifications ?? [];
  return {
    eventId: ev.id,
    kind,
    title: ev.title,
    startDate,
    endDate,
    startTime: startsAtDate ? instantToTimeStr(startsAtDate, tz) : '',
    endTime: endsAtDate && !ev.allDay ? instantToTimeStr(endsAtDate, tz) : '',
    allDay: ev.allDay,
    includeFromDaysBefore:
      includeFromDaysBefore == null ? '0' : String(includeFromDaysBefore),
    notifications: [...notifications],
    recurrence: ev.recurrence ?? 'none',
  };
}

/** Pulls the event fields off a source event for use in
 * drag/resize-driven upsertEvent calls. */
function newEventFields(ev: EventWithId): {
  kind: 'event' | 'interval' | 'reminder';
  includeFromDaysBefore?: number;
  notifications?: typeof ev.notifications;
} {
  return {
    kind: ev.kind ?? 'event',
    includeFromDaysBefore: ev.includeFromDaysBefore,
    notifications: ev.notifications,
  };
}

/** Custom FullCalendar event renderer. Adds a small bell glyph in
 * front of the title for events that have any notifications
 * configured, so caregivers can scan the calendar and see which
 * events will fire (without opening each one). Otherwise renders
 * the same time + title as the default. */
function renderEventContent(arg: EventContentArg) {
  const hasNotifications = Boolean(arg.event.extendedProps['hasNotifications']);
  return (
    <div className="fc-event-main-frame">
      {arg.timeText && (
        <div className="fc-event-time">{arg.timeText}</div>
      )}
      <div className="fc-event-title-container">
        <div className="fc-event-title fc-sticky">
          {hasNotifications && (
            <span
              className="rmv-event-bell"
              aria-label="Has notifications"
              title="Has notifications"
            >
              🔔
            </span>
          )}
          {arg.event.title || <>&nbsp;</>}
        </div>
      </div>
    </div>
  );
}

// FullCalendar runs in local/floating mode, so a Date it hands back (from a
// drag/resize) carries the new wall-clock in the account tz's LABEL space —
// its browser-local components equal the grid label the user dropped it on.
// Re-anchor that wall-clock to the account tz to get the true UTC instant.
// (Timestamp.fromDate(d) directly would store the browser-local reading — the
// #141 bug.) select/click reads use the same toIsoLocal/toHmLocal label
// extraction and are interpreted as account-tz on save, so they round-trip.
function fcDateToInstant(d: Date, tz: string): Timestamp {
  return Timestamp.fromDate(fcLocalDateToInstant(d, tz));
}

// `toIsoLocal`/`toHmLocal` read a FullCalendar (local-mode) Date's wall-clock
// LABEL — deliberately browser-local, because that label is what the user
// clicked/dragged on the grid; callers re-interpret it as account-tz.
function toIsoLocal(d: Date): string {
  // Local YYYY-MM-DD; .toISOString() shifts to UTC and can land on the
  // wrong day for users east of UTC.
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function toHmLocal(d: Date): string {
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}

